import { formatQuantity } from '@fewerbags/core';
import type { GroceryItemDto } from '@fewerbags/schema';
import {
  formatPriceCents,
  isChecked,
  isHave,
  locationLabel,
  needVsBuyLabel,
  reportableValues,
  suggestionBasis,
  unconvertedHint,
  usesHouseholdOverride,
} from '../../pages/groceries/display';

/**
 * One shopping-list row: checkbox, "have it" (D35), name, quantity/unit,
 * hints, location and price.
 */
export function GroceryItemRow({
  item,
  onToggle,
  onHave,
  onEdit,
  onRemove,
  onReport,
  storeId = null,
  busy = false,
}: {
  item: GroceryItemDto;
  onToggle: (item: GroceryItemDto) => void;
  /** "I have this at home" — resolve without buying. Shown on unchecked rows only. */
  onHave?: ((item: GroceryItemDto) => void) | undefined;
  onEdit: (item: GroceryItemDto) => void;
  onRemove: (item: GroceryItemDto) => void;
  /** Report the shared price/location shown on this row (PLAN §9 #3c). */
  onReport?: ((item: GroceryItemDto) => void) | undefined;
  storeId?: string | null;
  busy?: boolean;
}) {
  const checked = isChecked(item);
  const have = isHave(item);
  const needVsBuy = needVsBuyLabel(item);
  const loc = locationLabel(item.location);
  const price = formatPriceCents(item.priceCents);
  const unconverted = unconvertedHint(item);
  const override = usesHouseholdOverride(item);
  const reportable = onReport ? reportableValues(item, storeId) : [];

  return (
    <li
      className={`card flex items-start gap-1.5 p-2 pr-1 transition-colors ${checked ? 'bg-cream/70' : ''}`}
    >
      <button
        type="button"
        aria-pressed={checked}
        aria-label={checked ? `Uncheck ${item.ingredientName}` : `Check off ${item.ingredientName}`}
        disabled={busy}
        onClick={() => onToggle(item)}
        className="group flex size-11 shrink-0 items-center justify-center rounded-full disabled:opacity-50"
      >
        <span
          className={`flex size-7 items-center justify-center rounded-full border-2 transition-colors ${
            checked
              ? 'border-brand bg-brand text-white'
              : 'border-brand/40 bg-surface group-hover:border-brand group-hover:bg-accent-soft'
          }`}
        >
          {checked ? (
            <svg viewBox="0 0 20 20" className="size-4" fill="none" aria-hidden="true">
              <path
                d="M4 10.5l3.5 3.5L16 6"
                stroke="currentColor"
                strokeWidth={2.5}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ) : null}
        </span>
      </button>

      {onHave && !checked ? (
        <button
          type="button"
          aria-label={`I have ${item.ingredientName} at home`}
          title="Have it — already at home, no need to buy"
          disabled={busy}
          onClick={() => onHave(item)}
          className="flex size-11 shrink-0 flex-col items-center justify-center rounded-full text-brand/70 transition-colors hover:bg-accent-soft hover:text-brand disabled:opacity-50"
        >
          <svg viewBox="0 0 20 20" className="size-5" fill="none" aria-hidden="true">
            <path
              d="M3 9.5L10 4l7 5.5M5 8v7.5h10V8"
              stroke="currentColor"
              strokeWidth={1.8}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span aria-hidden className="text-[10px] leading-3 font-medium">
            Have
          </span>
        </button>
      ) : null}

      <button
        type="button"
        onClick={() => onEdit(item)}
        className="min-h-11 min-w-0 flex-1 rounded-lg px-1 py-1.5 text-left"
        aria-label={`Edit ${item.ingredientName}`}
      >
        <span
          className={`block truncate text-base font-medium ${checked ? 'text-muted line-through' : 'text-ink'}`}
        >
          {item.ingredientName}
        </span>
        <span className="block text-sm text-muted">
          {needVsBuy ?? (
            <>
              {formatQuantity(item.buyQty, item.unit)}
              {item.productLabel ? ` · ${item.productLabel}` : ''}
            </>
          )}
          {unconverted ? (
            <span
              title={unconverted}
              className="ml-2 inline-block rounded border border-line px-1.5 align-middle text-[11px] leading-4 text-muted"
            >
              unconverted<span className="sr-only">: {unconverted}</span>
            </span>
          ) : null}
        </span>
        {item.mealLabels.length > 0 ? (
          <span className="block text-xs text-muted">for: {item.mealLabels.join(', ')}</span>
        ) : null}
        {item.note ? <span className="block text-xs text-muted italic">{item.note}</span> : null}
        {have ? (
          <span className="mt-1 flex flex-wrap items-center gap-2">
            <span
              title="Already at home — nothing bought"
              className="rounded border border-accent/50 bg-accent-soft px-1.5 text-[11px] leading-4 font-medium text-accent-ink"
            >
              pantry
            </span>
          </span>
        ) : loc || price ? (
          <span className="mt-1 flex flex-wrap items-center gap-2">
            {loc ? (
              <span
                className={`text-xs ${item.location?.suggested ? 'italic text-muted' : 'font-medium text-brand'}`}
                title={item.location?.suggested ? suggestionBasis(item.location.source) : undefined}
              >
                {loc}
                {item.location?.suggested ? ' (suggested)' : ''}
              </span>
            ) : null}
            {price ? <span className="text-xs text-muted">{price}</span> : null}
            {override ? (
              <span
                title="Your household's own price or location for this store"
                className="rounded border border-line px-1.5 text-[11px] leading-4 text-muted"
              >
                override
              </span>
            ) : null}
          </span>
        ) : null}
      </button>
      {reportable.length > 0 ? (
        <button
          type="button"
          onClick={() => onReport?.(item)}
          aria-label={`Report the shared ${reportable.join(' or ')} for ${item.ingredientName}`}
          className="mt-1 shrink-0 self-end text-xs text-muted underline hover:text-fail"
        >
          Report
        </button>
      ) : null}

      <button
        type="button"
        aria-label={`Remove ${item.ingredientName} from the list`}
        onClick={() => onRemove(item)}
        className="flex size-11 shrink-0 items-center justify-center rounded-full text-lg text-muted transition-colors hover:bg-red-50 hover:text-fail"
      >
        <span aria-hidden>×</span>
      </button>
    </li>
  );
}
